/**
 * IHS-3 Victory probability — compact single-bar layout.
 *
 * Privacy-safe: the engine pre-strips the bookmaker margin and ships
 * normalised 0..1 percentages.
 *
 * Layout:
 *   [home %] [—————— blue|grey|orange bar ——————] [away %]
 *   • Home Team Name                    Away Team Name •
 */

import type { ReactElement } from 'react';
import { useTranslation } from '../../../i18n/index.js';
import { useTenantConfig } from '../../../tenant/useTenantConfig.js';
import type { IhsVictoryProbabilityData } from '../../../types/ihs.js';
import { formatPercent } from '../../../utils/numbers.js';
import { IhsCard } from './IhsCard.js';
import { LegendRow } from '../../primitives/LegendRow.js';
import { SegmentedBar } from '../../primitives/SegmentedBar.js';
import { TEAM_HOME_COLOR, TEAM_AWAY_COLOR } from '../../../styles/team-colors.js';
import styles from './IhsVictoryProbability.module.css';

const HOME_COLOR = TEAM_HOME_COLOR;
const AWAY_COLOR = TEAM_AWAY_COLOR;

export function IhsVictoryProbability({
  data,
}: {
  data: IhsVictoryProbabilityData;
}): ReactElement {
  const { t } = useTranslation();
  const tenant = useTenantConfig();
  const locale =
    tenant.kind === 'live' || tenant.kind === 'paused'
      ? tenant.config.market.locale
      : 'en-GB';

  // Normalise (in case the three percentages don't sum to 1 due to
  // rounding). When the engine ships placeholder data we paint a flat
  // 33/33/33 split rather than three zero-width segments.
  const sum = data.homePct + data.drawPct + data.awayPct;
  const homeW = sum > 0 ? (data.homePct / sum) * 100 : 33.3;
  const drawW = sum > 0 ? (data.drawPct / sum) * 100 : 33.3;
  const awayW = sum > 0 ? (data.awayPct / sum) * 100 : 33.3;

  const title =
    data.header?.title ??
    t('ihs.victoryProbability.title', { defaultValue: 'Victory probability' });
  const subtitle =
    data.header?.subtitle ??
    t('ihs.victoryProbability.subtitle', { defaultValue: 'Based on odds' });

  const homePctText = formatPercent(data.homePct, locale);
  const awayPctText = formatPercent(data.awayPct, locale);

  return (
    <IhsCard title={title} subtitle={subtitle}>
      <div className={`${styles.row} stage-row`}>
        <div className={styles.pctBlock}>
          <span className={styles.pctValue}>{homePctText}</span>
        </div>
        <SegmentedBar
          ariaLabel={t('ihs.victoryProbability.barAria', {
            defaultValue: 'Win probability shares',
          })}
          segments={[
            { width: homeW, color: HOME_COLOR },
            { width: drawW, color: 'var(--stat-bar-bg, #EDF0F5)' },
            { width: awayW, color: AWAY_COLOR },
          ]}
        />
        <div className={styles.pctBlock}>
          <span className={styles.pctValue}>{awayPctText}</span>
        </div>
      </div>
      <div className={styles.legendWrap}>
        <LegendRow
          items={[
            { label: data.home.name, color: HOME_COLOR },
            { label: data.away.name, color: AWAY_COLOR, position: 'right' },
          ]}
        />
      </div>
    </IhsCard>
  );
}
